import {
  camadaNoPonto,
  cantosDaCaixa,
  girarPonto,
  girarPorPonteiro,
  moverCamada,
  redimensionarPorAlca,
  transformarPorPinca,
  type AlcaDeCanto,
  type Camada,
  type CaixaDaCamada,
  type Ponto,
  type Tamanho,
} from "@foto/shared";

/**
 * Os gestos do editor de camadas, sem DOM: qual gesto um toque começa e o que ele faz com a camada a cada
 * movimento do ponteiro.
 *
 * Toda a geometria vem de `@foto/shared` (`moverCamada`, `redimensionarPorAlca`, `girarPorPonteiro`,
 * `transformarPorPinca`): aqui só se decide **qual** delas chamar e se monta o pedido. Os pontos são em
 * pixels da célula, na tela, com a origem no canto superior esquerdo dela.
 *
 * Todo gesto parte **sempre da camada do início do gesto** — as funções de `@foto/shared` esperam isso, e é o que
 * evita o erro se acumular a cada movimento.
 */

/** A folga do clique, em px: o dedo é mais largo que o ponteiro, e a caixa de uma figurinha pequena é pouca coisa. */
export const FOLGA_DO_TOQUE = 10;
/** O raio de alcance de cada alça, em px: o alvo de toque de 44 px, com a alça no meio. */
export const RAIO_DA_ALCA = 22;
/** Quanto as alças de canto ficam para fora do canto, em px: não tampam a figurinha pequena. */
export const AFASTAMENTO_DA_ALCA = 10;
/** A distância da alça de girar à borda de cima da caixa, em px. */
export const DISTANCIA_DA_ALCA_DE_GIRAR = 28;
/** Até quanto o ponteiro pode andar num toque no fundo para ainda contar como "toque", em px. */
export const TOLERANCIA_DO_TOQUE = 6;

const ALCAS: readonly AlcaDeCanto[] = ["nw", "ne", "se", "sw"];

/** O índice de cada alça em `cantosDaCaixa`: superior esquerdo, superior direito, inferior direito, inferior esquerdo. */
const INDICE_DO_CANTO: Record<AlcaDeCanto, number> = { nw: 0, ne: 1, se: 2, sw: 3 };

export interface PosicoesDasAlcas {
  /** O canto da caixa, já girado: onde a alça "puxa". */
  cantos: Record<AlcaDeCanto, Ponto>;
  /** Onde cada alça de canto se desenha (um pouco para fora do canto). */
  alcas: Record<AlcaDeCanto, Ponto>;
  /** A alça de girar, acima do meio da borda de cima. */
  girar: Ponto;
  /** O meio da borda de cima (de onde sai a haste da alça de girar). */
  meioDeCima: Ponto;
}

/** Onde estão as alças de uma caixa. Tudo já girado com a caixa. */
export function posicoesDasAlcas(caixa: CaixaDaCamada): PosicoesDasAlcas {
  const c = cantosDaCaixa(caixa);
  const centro: Ponto = { x: caixa.cx, y: caixa.cy };
  const cantos = { nw: c[0], ne: c[1], se: c[2], sw: c[3] } as Record<AlcaDeCanto, Ponto>;

  const alcas = {} as Record<AlcaDeCanto, Ponto>;
  for (const alca of ALCAS) {
    const canto = c[INDICE_DO_CANTO[alca]]!;
    const dx = canto.x - centro.x;
    const dy = canto.y - centro.y;
    const dist = Math.hypot(dx, dy) || 1;
    alcas[alca] = {
      x: canto.x + (dx / dist) * AFASTAMENTO_DA_ALCA,
      y: canto.y + (dy / dist) * AFASTAMENTO_DA_ALCA,
    };
  }

  const meioDeCima = girarPonto(
    { x: centro.x, y: centro.y - caixa.altura / 2 },
    centro,
    caixa.rotacao,
  );
  const girar = girarPonto(
    { x: centro.x, y: centro.y - caixa.altura / 2 - DISTANCIA_DA_ALCA_DE_GIRAR },
    centro,
    caixa.rotacao,
  );
  return { cantos, alcas, girar, meioDeCima };
}

const distancia = (a: Ponto, b: Ponto): number => Math.hypot(a.x - b.x, a.y - b.y);

/** O que há sob o ponto numa camada selecionada: a alça de girar, uma alça de canto ou nada. */
export type AlcaNoPonto = { tipo: "girar" } | { tipo: "canto"; alca: AlcaDeCanto } | null;

export function alcaNoPonto(
  ponto: Ponto,
  caixa: CaixaDaCamada,
  raio: number = RAIO_DA_ALCA,
): AlcaNoPonto {
  const pos = posicoesDasAlcas(caixa);
  // A de girar primeiro: está longe das outras, mas numa caixa muito pequena as áreas se tocam.
  if (distancia(ponto, pos.girar) <= raio) return { tipo: "girar" };
  let melhor: { alca: AlcaDeCanto; d: number } | null = null;
  for (const alca of ALCAS) {
    const d = distancia(ponto, pos.alcas[alca]);
    if (d <= raio && (!melhor || d < melhor.d)) melhor = { alca, d };
  }
  return melhor ? { tipo: "canto", alca: melhor.alca } : null;
}

// ─── Começar um gesto ─────────────────────────────────────────────────────────

/** Uma camada e a sua caixa na célula, como o editor a tem agora. */
export interface ItemDaCelula {
  id: string;
  camada: Camada;
  caixa: CaixaDaCamada;
}

export type Gesto =
  | { tipo: "mover"; id: string; inicial: Camada; inicio: Ponto }
  | {
      tipo: "alca";
      id: string;
      inicial: Camada;
      caixa: CaixaDaCamada;
      alca: AlcaDeCanto;
      /** De quanto o toque errou o canto: o canto segue o ponteiro menos isto, e a camada não "pula" ao pegar. */
      desvio: Ponto;
    }
  | { tipo: "girar"; id: string; inicial: Camada; centro: Ponto; inicio: Ponto }
  | {
      tipo: "pinca";
      id: string;
      inicial: Camada;
      /** Os dois dedos quando a pinça começou. */
      inicio: readonly [Ponto, Ponto];
    }
  /** Um toque no vazio: tira a seleção quando solta sem ter andado. */
  | { tipo: "fundo"; inicio: Ponto; moveu: boolean };

/**
 * O gesto que o toque em `ponto` começa. Primeiro as alças da camada selecionada (as de canto esticam, a de
 * girar gira); depois a camada de cima que o toque acerta (com folga), que o gesto passa a mover; senão, o
 * fundo.
 */
export function iniciarGesto(
  ponto: Ponto,
  itens: readonly ItemDaCelula[],
  selecionadaId: string | null,
): Gesto {
  const selecionada = selecionadaId ? itens.find((i) => i.id === selecionadaId) : undefined;
  if (selecionada) {
    const alca = alcaNoPonto(ponto, selecionada.caixa);
    if (alca?.tipo === "girar") {
      return {
        tipo: "girar",
        id: selecionada.id,
        inicial: selecionada.camada,
        centro: { x: selecionada.caixa.cx, y: selecionada.caixa.cy },
        inicio: ponto,
      };
    }
    if (alca?.tipo === "canto") {
      const canto = posicoesDasAlcas(selecionada.caixa).cantos[alca.alca];
      return {
        tipo: "alca",
        id: selecionada.id,
        inicial: selecionada.camada,
        caixa: selecionada.caixa,
        alca: alca.alca,
        desvio: { x: ponto.x - canto.x, y: ponto.y - canto.y },
      };
    }
  }

  const id = camadaNoPonto(
    itens.map((i) => ({ id: i.id, caixa: i.caixa })),
    ponto,
    FOLGA_DO_TOQUE,
  );
  const alvo = id ? itens.find((i) => i.id === id) : undefined;
  if (alvo) return { tipo: "mover", id: alvo.id, inicial: alvo.camada, inicio: ponto };
  return { tipo: "fundo", inicio: ponto, moveu: false };
}

/** A pinça de dois dedos sobre a camada selecionada. */
export function iniciarPinca(
  item: ItemDaCelula,
  dedos: readonly [Ponto, Ponto],
): Extract<Gesto, { tipo: "pinca" }> {
  return { tipo: "pinca", id: item.id, inicial: item.camada, inicio: dedos };
}

// ─── Continuar o gesto ────────────────────────────────────────────────────────

export interface Teclas {
  /** Shift: no giro, encaixa de 15° em 15°. */
  shift?: boolean;
  /** Alt/Option: ao esticar, a partir do centro. */
  alt?: boolean;
}

/**
 * A camada que o gesto produz com o ponteiro onde está agora. `pontos` é o ponteiro (um) ou os dois dedos da
 * pinça, na mesma ordem em que a pinça começou. `null` quando o gesto não mexe em camada (o toque no fundo).
 */
export function aplicarGesto(
  gesto: Gesto,
  pontos: readonly Ponto[],
  celula: Tamanho,
  teclas: Teclas = {},
): Camada | null {
  const p = pontos[0];
  if (!p) return null;

  switch (gesto.tipo) {
    case "mover":
      return moverCamada(
        gesto.inicial,
        { x: p.x - gesto.inicio.x, y: p.y - gesto.inicio.y },
        celula,
      );

    case "alca":
      return redimensionarPorAlca(
        gesto.inicial,
        gesto.caixa,
        gesto.alca,
        { x: p.x - gesto.desvio.x, y: p.y - gesto.desvio.y },
        celula,
        { aPartirDoCentro: teclas.alt === true },
      );

    case "girar":
      return girarPorPonteiro(gesto.inicial, gesto.centro, gesto.inicio, p, {
        encaixe: teclas.shift === true,
      });

    case "pinca": {
      const q = pontos[1];
      return q ? transformarPorPinca(gesto.inicial, gesto.inicio, [p, q], celula) : null;
    }

    case "fundo":
      return null;
  }
}

/** O toque no fundo andou o bastante para deixar de ser um "toque"? */
export const andouDemais = (inicio: Ponto, atual: Ponto): boolean =>
  distancia(inicio, atual) > TOLERANCIA_DO_TOQUE;

/** O cursor que o ponteiro (mouse) deve mostrar sobre `ponto`. */
export function cursorNoPonto(
  ponto: Ponto,
  itens: readonly ItemDaCelula[],
  selecionadaId: string | null,
): "default" | "move" | "grab" | "nwse-resize" | "nesw-resize" {
  const gesto = iniciarGesto(ponto, itens, selecionadaId);
  switch (gesto.tipo) {
    case "mover":
      return "move";
    case "girar":
      return "grab";
    case "alca":
      return gesto.alca === "nw" || gesto.alca === "se" ? "nwse-resize" : "nesw-resize";
    default:
      return "default";
  }
}
